// Login-screen sponsor rotation.
//
// The Sign In screen is PRE-AUTH, so it can't read the private `sponsors/{uid}`
// profiles (those hold email/role and are sign-in-only). Instead the dashboard
// materializes display-safe sponsor ads into a dedicated public collection
// `loginSponsors/{id}` — mirroring how it already materializes standings into
// `leaderboards/{tid}`. Only display fields live there, so it's safe to expose.
//
// Expected doc shape (all the dashboard needs to write):
//   loginSponsors/{autoId} {
//     imageUrl: string,   // REQUIRED — the sponsor image to display
//     linkUrl?: string,   // optional — tapping the image opens this
//     name?:    string,   // optional — used as alt text / label
//     tier?:    string,   // optional — 'series' | 'regional' | 'local' | 'house'
//     weight?:  number,   // optional — share-of-voice: 1..3 repeats in the band
//     priority?: number,  // optional — sort/pin rank (higher shows first)
//     active:   boolean,  // REQUIRED — true only while in the paid window
//   }
// Flip `active` false (or delete the doc) when a sponsor's window ends.

import { collection, getDocs, getDocsFromServer, query, where } from "firebase/firestore";
import { db } from "../firebase";

const CACHE_KEY = "the99.loginSponsors.v1";

// Tolerate a few field-name variants so the login screen keeps working even if
// the dashboard names things slightly differently.
function normalize(raw = {}) {
  const imageUrl = String(raw.imageUrl || raw.logoUrl || raw.image || "").trim();
  if (!imageUrl) return null; // no image → nothing to show
  return {
    imageUrl,
    linkUrl: String(raw.linkUrl || raw.url || raw.website || raw.link || "").trim(),
    name: String(raw.name || raw.sponsorName || raw.companyName || raw.title || "Sponsor").trim(),
    tier: String(raw.tier || "").trim().toLowerCase(),
    // weight = share-of-voice (times the logo repeats in the scrolling band).
    weight: Math.max(1, Math.min(3, Number(raw.weight) || 1)),
    // priority = sort/pin rank (higher first). Falls back to weight.
    priority: Number(raw.priority ?? raw.weight ?? 0) || 0,
  };
}

function shuffle(list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Model 1 selection: Series is always pinned first; Regional/Local (and any
// house sponsors) fill the remaining slots via a per-session weighted-random
// shuffle so lower tiers get fair rotation over time. Capped to MARQUEE_CAP
// DISTINCT sponsors. Share-of-voice (weight) is applied later in SponsorBand.
const MARQUEE_CAP = 6;
function selectMarquee(list, cap = MARQUEE_CAP) {
  const series = list.filter((s) => s.tier === "series").sort((a, b) => b.priority - a.priority);
  // Shuffle first (rotation), then a stable sort keeps higher-priority tiers
  // ahead while randomizing ties within a tier.
  const others = shuffle(list.filter((s) => s.tier !== "series"))
    .sort((a, b) => b.priority - a.priority);
  const pinned = series.slice(0, cap);
  const fill = others.slice(0, Math.max(0, cap - pinned.length));
  return [...pinned, ...fill];
}

/** Synchronously read the last-known sponsors from localStorage for an instant
 * (and offline) first paint. Returns [] on any problem. */
export function readCachedLoginSponsors() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCache(list) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(list));
  } catch {
    /* storage blocked — non-fatal */
  }
}

/** Fetch active login sponsors from Firestore (public read), normalize, cache,
 * and return them sorted by weight. Never throws — returns the cached list on
 * failure so the band still renders what it had.
 *
 * IMPORTANT: reads from the SERVER first (getDocsFromServer). With Firestore's
 * persistentLocalCache enabled, a plain getDocs can return a stale cached query
 * (e.g. only the first sponsor that existed when the cache was last populated),
 * which is exactly why a newly-added 2nd sponsor didn't appear. We fall back to
 * the cached getDocs only when the server is unreachable (offline). */
export async function fetchLoginSponsors() {
  const runQuery = query(collection(db, "loginSponsors"), where("active", "==", true));
  try {
    let snap;
    try {
      snap = await getDocsFromServer(runQuery); // fresh, bypasses stale local cache
    } catch (serverErr) {
      // Offline (or transient) — fall back to whatever the local cache has.
      snap = await getDocs(runQuery);
    }
    const list = selectMarquee(
      snap.docs.map((d) => normalize(d.data())).filter(Boolean)
    );
    writeCache(list);
    return list;
  } catch (err) {
    console.warn("[loginSponsors] fetch failed:", err?.message || err);
    return readCachedLoginSponsors();
  }
}
